import { combineSlices, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { addBreadcrumb, logError } from '~/utils/observability';
import { gracefulStorage } from '~/utils/storage';
import { crewClaimSlice, crewClaimStateSchema } from './slices/crewClaim';
import {
  draftClaimLineItemSlice,
  draftClaimLineItemStateSchema,
} from './slices/draftClaimLineItem';
import { orderSlice, orderStateSchema } from './slices/order';
import { orderEditSlice, orderEditStateSchema } from './slices/orderEdit';
import {
  registrationsSlice,
  registrationsStateSchema,
} from './slices/registration';
import { settingsSlice, settingsStateSchema } from './slices/settings';
import * as sharedActions from './slices/sharedActions';
import {
  shippingProtectionClaimSlice,
  shippingProtectionClaimStateSchema,
} from './slices/shippingProtectionClaim';

export const stateSchema = z.object({
  [settingsSlice.reducerPath]: settingsStateSchema,
  [orderSlice.reducerPath]: orderStateSchema,
  [registrationsSlice.reducerPath]: registrationsStateSchema,
  [crewClaimSlice.reducerPath]: crewClaimStateSchema,
  [draftClaimLineItemSlice.reducerPath]: draftClaimLineItemStateSchema,
  [shippingProtectionClaimSlice.reducerPath]:
    shippingProtectionClaimStateSchema,
  [orderEditSlice.reducerPath]: orderEditStateSchema,
});
const persistedStateSchema = stateSchema.omit({ settings: true, order: true });
type PersistedState = z.infer<typeof persistedStateSchema>;

export const createAppStore = (
  initialState?: AppState | PersistedState | null,
) => {
  const store = configureStore({
    preloadedState: initialState ?? undefined,
    reducer: combineSlices(
      settingsSlice,
      orderSlice,
      crewClaimSlice,
      draftClaimLineItemSlice,
      registrationsSlice,
      shippingProtectionClaimSlice,
      orderEditSlice,
    ),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(Sentry.createReduxEnhancer()),
  });

  const appStore = {
    ...store,
    actions: {
      ...sharedActions,
      ...orderSlice.actions,
      ...settingsSlice.actions,
      [registrationsSlice.reducerPath]: registrationsSlice.actions,
      [crewClaimSlice.reducerPath]: crewClaimSlice.actions,
      [draftClaimLineItemSlice.reducerPath]: draftClaimLineItemSlice.actions,
      [shippingProtectionClaimSlice.reducerPath]:
        shippingProtectionClaimSlice.actions,
      [orderEditSlice.reducerPath]: orderEditSlice.actions,
    },
  };

  return appStore;
};

const storageDataSchema = z
  .object({
    expiresAt: z.number(),
    data: persistedStateSchema,
  })
  .refine((data) => Date.now() < data.expiresAt, {
    message: 'Persisted data from localStorage has expired',
  });

const storage = gracefulStorage('localStorage');
const key = 'corso-app';

const persistState = (store: ReturnType<typeof createAppStore>) => () => {
  const expiryDurationMs = 1000 * 60 * 60; // 1 hour
  const expiresAt = Date.now() + expiryDurationMs;
  const { settings, order, ...stateToPersist } = store.getState();

  storage.setItem(
    key,
    JSON.stringify({ expiresAt, data: stateToPersist } satisfies z.infer<
      typeof storageDataSchema
    >),
  );
};

const retrieveState = () => {
  const raw = storage.getItem(key);
  if (!raw) return null;

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      message: 'Persisted data from localStorage is not valid JSON',
    });
    return null;
  }

  return match(storageDataSchema.safeParse(parsed))
    .with({ success: false }, ({ error }) => {
      addBreadcrumb({
        type: 'info',
        level: 'info',
        category: 'AppStore',
        message: error.message,
        data: {
          error,
        },
      });
      return null;
    })
    .with({ success: true }, ({ data }) => data.data)
    .exhaustive();
};

export const initializeAppStore = () => {
  let state: ReturnType<typeof retrieveState> = null;
  try {
    state = retrieveState();
  } catch (error) {
    logError(
      error instanceof Error
        ? error
        : new Error('Failed to retrieve persisted app store state', {
            cause: error,
          }),
      {
        extra: {
          retrievedValues: storage.getItem(key),
        },
      },
    );
  }
  const appStore = createAppStore(state);
  appStore.subscribe(persistState(appStore));
  return appStore;
};

export type AppState = z.infer<typeof stateSchema>;
export type AppStore = ReturnType<typeof createAppStore>;
